import * as Sentry from "@sentry/react";
import type { RootOptions } from "react-dom/client";
import { isChunkLoadError } from "@/shared/lib/chunkReloadRecovery";

// write-only ingest 키 — 비밀 아님(클라 번들에 노출되는 게 정상). apiBase.ts 와 같은 committed-config 규약.
const SENTRY_DSN =
  "https://39f8a3c78e3a6d484a1274b686c319d6@o4511861453094912.ingest.us.sentry.io/4511861498576896";

// 배포 대상 MODE 만 허용. import.meta.env.PROD 만 보면 mode 없는 기본 `pnpm build`(MODE="production")가
// environment="production" 유령 환경으로 새어 들어오므로 배포 MODE 로 좁힌다.
// ⚠️ === 비교 유지(배열 .includes 금지): import.meta.env.PROD/MODE 는 Vite 가 컴파일타임 리터럴로
//    치환하므로, === 로 두면 minifier 가 SENTRY_ENABLED 를 상수 접기(fold)해 Sentry 가 꺼지는
//    빌드(로컬 dev·기본 production)에선 SDK 코드가 통째로 트리셰이크된다. .includes 는 접히지 않아 잔존한다.
const SENTRY_ENABLED =
  import.meta.env.PROD &&
  (import.meta.env.MODE === "dev" ||
    import.meta.env.MODE === "stg" ||
    import.meta.env.MODE === "prd");

// 백엔드 에러 메시지가 입력값(주민번호/전화/이메일)을 echo 할 수 있어, Sentry 로 나가는 이벤트 문자열에서
// PII 를 마스킹한다. 숫자 ID 등 일부 과잉 마스킹은 감수한다(PII 안전 > 정보 완전성).
const PII_PATTERNS: RegExp[] = [
  /\d{6}-?\d{7}/g, // 주민등록번호 (13자리, dash 유무)
  /01[0-9][-\s]?\d{3,4}[-\s]?\d{4}/g, // 휴대폰 (하이픈/공백/무구분)
  /[\w.+-]+@[\w-]+\.[\w.-]+/g, // 이메일
  /\d{10,}/g, // 10자리 이상 연속 숫자 (기타 ID·미형식 번호)
];
const scrubPii = (value: string): string =>
  PII_PATTERNS.reduce((acc, re) => acc.replace(re, "[redacted]"), value);

// URL 에서 쿼리스트링·프래그먼트 제거(경로만 남김) — 쿼리에 추천코드/OAuth code·state, 프래그먼트에
// implicit OAuth access_token 등 민감값이 실릴 수 있다.
const stripQueryFragment = (url: string): string => url.split(/[?#]/)[0];

/** 앱 부팅 시 1회 호출. 배포 대상 빌드(dev/stg/prd)가 아니면 no-op. */
export const initSentry = (): void => {
  if (!SENTRY_ENABLED) return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: __APP_RELEASE__,
    sendDefaultPii: false, // 주민번호·보험 등 PII 자동 수집 차단
    // browserTracing/Replay 미사용 — 목표는 에러 수집이다. 트레이스 span URL 이 쿼리(추천코드 등)를
    //   실어 PII 누출면을 넓히고 저사양 WebView 부담이 있어 제외.
    // console breadcrumb 비활성 — logger 가 error 객체를, RouteLogger 가 pathname+search(OAuth code/state)를
    //   콘솔로 남겨 breadcrumb message/arguments 로 PII 가 샌다. 기본 통합에서 console 만 끄고 나머지는 유지한다.
    integrations: (defaults) =>
      defaults.map((integration) =>
        integration.name === "Breadcrumbs"
          ? Sentry.breadcrumbsIntegration({ console: false })
          : integration,
      ),
    // ① 청크 자체복구(#198) 에러는 self-heal 신호라 drop.
    // ② 이벤트 문자열(message/exception/breadcrumb)의 PII 마스킹 — 백엔드 메시지가 입력값을 echo 하는 경우 대비.
    // ③ 현재 페이지 URL(httpContextIntegration)·Referer 의 쿼리·프래그먼트 제거 — OAuth code/state·token 방지.
    beforeSend: (event, hint) => {
      if (isChunkLoadError(hint?.originalException)) return null;
      if (event.message) event.message = scrubPii(event.message);
      event.exception?.values?.forEach((value) => {
        if (value.value) value.value = scrubPii(value.value);
      });
      event.breadcrumbs?.forEach((breadcrumb) => {
        if (breadcrumb.message) breadcrumb.message = scrubPii(breadcrumb.message);
      });
      const request = event.request;
      if (request) {
        if (typeof request.url === "string") request.url = stripQueryFragment(request.url);
        const headers = request.headers;
        if (headers) {
          for (const key of Object.keys(headers)) {
            if (key.toLowerCase() === "referer" && typeof headers[key] === "string") {
              headers[key] = stripQueryFragment(headers[key]);
            }
          }
        }
      }
      return event;
    },
    // fetch/xhr url, navigation from/to 의 쿼리·프래그먼트 제거(?code=<추천코드> 등).
    beforeBreadcrumb: (breadcrumb) => {
      const data = breadcrumb.data;
      if (data) {
        for (const key of ["url", "from", "to"]) {
          const raw = data[key];
          if (typeof raw === "string") data[key] = stripQueryFragment(raw);
        }
      }
      return breadcrumb;
    },
  });
};

/**
 * React 19 root-level 에러 캡처용 createRoot 옵션.
 * - 활성 빌드에서만 핸들러를 건다. 비활성(로컬 dev 등)은 {} 반환 → React 기본 콘솔 로깅 보존.
 * - onCaughtError 는 의도적으로 미배선: RouteErrorBoundary(#198)가 caught 를 소유하고 non-chunk 를
 *   rethrow 해 onUncaughtError 로 1회 수렴, chunk 는 beforeSend 에서 drop → 이중 보고 회피.
 */
export const sentryRootOptions = (): RootOptions =>
  SENTRY_ENABLED
    ? {
        onUncaughtError: Sentry.reactErrorHandler(),
        onRecoverableError: Sentry.reactErrorHandler(),
      }
    : {};
